import { Injectable, signal } from '@angular/core';
import { TranslationKey } from '@core/utils/marker';
import { ApiBanner } from '@e-check/shared';

export type Banner = ApiBanner & {
  id: string;
  translationKey: TranslationKey;
};

@Injectable({ providedIn: 'root' })
export class BannerService {
  readonly banners = signal<Banner[]>([]);

  show(props: Omit<Banner, 'id'>): string {
    const id = crypto.randomUUID();
    this.banners.update((current) => [...current, { id, ...props }]);
    return id;
  }

  dismiss(id: string): void {
    const banner = this.banners().find((e) => e.id === id);
    if (!banner) return;

    addBannerToDismissedStore(banner);

    this.banners.update((current) => current.filter((b) => b.id !== id));
  }

  isShowing(banner: ApiBanner): boolean {
    const fingerprint = getBannerFingerPrint(banner);
    return this.banners().some((b) => getBannerFingerPrint(b) === fingerprint);
  }

  clear(): void {
    this.banners.set([]);
  }
}

function getBannerFingerPrint(banner: ApiBanner) {
  return banner.translationKey + JSON.stringify(banner.translationParams);
}

const BANNER_STORAGE_KEY = 'dismissed_banners';

function getDismissedFingerPrints() {
  return new Set<string>(JSON.parse(localStorage.getItem(BANNER_STORAGE_KEY) ?? '[]'));
}

export function addBannerToDismissedStore(banner: ApiBanner) {
  const fingerprint = getBannerFingerPrint(banner);
  const dismissedFingerPrints = getDismissedFingerPrints();
  dismissedFingerPrints.add(fingerprint);
  localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify([...dismissedFingerPrints]));
}

export function hasBannerBeenDismissed(banner: ApiBanner) {
  const fingerprint = getBannerFingerPrint(banner);
  const dismissedFingerPrints = getDismissedFingerPrints();
  return dismissedFingerPrints.has(fingerprint);
}
